/**
 * The Start instance: the one place every request passes through.
 *
 * TanStack Start picks this file up by name (`src/start.ts`, `startInstance`).
 * It exists for the crawler log — and, because declaring it replaced Start's
 * default list, for the CSRF middleware that list used to hold (below). The
 * crawler log is a request middleware, and the reason it is here rather than
 * in the root route's `beforeLoad` is coverage: `beforeLoad` runs for pages the
 * router renders, and the requests this middleware exists to see are mostly NOT
 * pages — a crawler reading `/llms.txt`, `/robots.txt` or `/sitemap.xml` hits a
 * server route that never enters the router.
 *
 * See `lib/crawlers.ts` for why the site needed a crawler log at all, and
 * `lib/crawlerLog.ts` for the contract it keeps: inert without its binding,
 * cannot throw, cannot hang, and runs after the response exists.
 */

import {
  createCsrfMiddleware,
  createMiddleware,
  createStart,
} from "@tanstack/react-start";

/**
 * Start's CSRF check for server functions, put back.
 *
 * With no `startInstance`, Start runs `[defaultCsrfMiddleware]` — this same
 * middleware with this same filter (`createStartHandler.js`). Declaring
 * `requestMiddleware` REPLACES that list rather than adding to it, so from the
 * day the crawler log was added the site had no CSRF check at all. Nothing
 * was exposed, because there are no server functions (and `lib/agents/
 * respond.ts` answers `/_serverFn/*` with a 404 before Start sees it) — but
 * the first `createServerFn` anybody added would have been a cross-site
 * callable endpoint with no warning in production, where Start's "not
 * protected by the CSRF middleware" message is compiled out. Found by the
 * 2026-10-27 security audit (h8-web-4).
 *
 * The filter keeps it to server-function requests: pages and the GET server
 * routes are not state-changing and are read cross-site by design (crawlers,
 * agents, link previews). First in the list so a refused request is refused
 * before anything else runs. `start.test.ts` pins it, against the real
 * middleware.
 */
const csrf = createCsrfMiddleware({
  filter: (ctx) => ctx.handlerType === "serverFn",
});

const crawlerLog = createMiddleware({ type: "request" }).server(
  async ({ request, pathname, next }) => {
    const result = await next();
    // Always true where this runs; written out so the client build, where Vite
    // replaces it with `false`, drops the import below as dead code — the same
    // guard the root route's `beforeLoad` uses for its server-only imports.
    if (!import.meta.env.SSR) return result;
    // Imported per request rather than at the top: this module is also part of
    // the client graph, and `crawlerLog.ts` imports `cloudflare:workers`,
    // which only exists in the Worker. After the first request it is a cached
    // module lookup. Any failure to load it is swallowed — a log line is never
    // worth a failed request.
    try {
      const { logCrawler } = await import("./lib/crawlerLog");
      logCrawler(request, pathname, result.response.status);
    } catch {
      // See above.
    }
    return result;
  },
);

export const startInstance = createStart(() => ({
  requestMiddleware: [csrf, crawlerLog],
}));
